'use client';

// Admin CRUD client — calls /api/admin/crud which uses service role key
// This bypasses RLS entirely, so admin operations always work regardless of RLS policies.

type AdminAction = 'insert' | 'update' | 'delete' | 'bulk_update' | 'select';

interface AdminCrudOptions {
  action: AdminAction;
  table: string;
  data?: Record<string, any>;
  id?: string | number;
  ids?: string[];
}

interface AdminCrudResult<T = any> {
  data?: T;
  error?: string;
  success?: boolean;
}

export async function adminCrud<T = any>(options: AdminCrudOptions): Promise<AdminCrudResult<T>> {
  try {
    const res = await fetch('/api/admin/crud', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(options),
    });
    const json = await res.json();
    if (!res.ok) {
      return { error: json.error ?? `HTTP ${res.status}` };
    }
    return json;
  } catch (err: any) {
    return { error: err.message ?? 'Errore di rete' };
  }
}

// ─── Convenience wrappers ─────────────────────────────────────────────────────

export const adminClimbsApi = {
  async getAll() {
    return adminCrud({ action: 'select', table: 'climbs', data: { order: 'name', ascending: true } });
  },
  async insert(row: Record<string, any>) {
    return adminCrud({ action: 'insert', table: 'climbs', data: row });
  },
  async update(id: string, row: Record<string, any>) {
    return adminCrud({ action: 'update', table: 'climbs', id, data: row });
  },
  async delete(id: string) {
    return adminCrud({ action: 'delete', table: 'climbs', id });
  },
};

export const adminUsersApi = {
  async getAll() {
    return adminCrud({ action: 'select', table: 'user_profiles', data: { order: 'joined_at', ascending: true } });
  },
  async update(id: string, row: Record<string, any>) {
    return adminCrud({ action: 'update', table: 'user_profiles', id, data: row });
  },
  async bulkUpdate(ids: string[], row: Record<string, any>) {
    return adminCrud({ action: 'bulk_update', table: 'user_profiles', ids, data: row });
  },
  async delete(id: string) {
    return adminCrud({ action: 'delete', table: 'user_profiles', id });
  },
};

export const adminEntriesApi = {
  async getAll() {
    return adminCrud({ action: 'select', table: 'climb_entries', data: { order: 'created_at', ascending: false } });
  },
  async update(id: string, row: Record<string, any>) {
    return adminCrud({ action: 'update', table: 'climb_entries', id, data: row });
  },
  async delete(id: string) {
    return adminCrud({ action: 'delete', table: 'climb_entries', id });
  },
};

export const adminSettingsApi = {
  async get() {
    return adminCrud({ action: 'select', table: 'club_settings' });
  },
  async update(row: Record<string, any>) {
    return adminCrud({ action: 'update', table: 'club_settings', id: 1, data: row });
  },
};
